import { useCallback, useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Skeleton from '@mui/material/Skeleton'
import { AlertTriangle } from 'react-feather'

import { EmailMultiSelect } from 'src/components/common/form-elements/EmailMultiSelect'
import { ExportConfigStep1, ExportConfigStep2 } from 'src/components/common/export-component/types'
import ExportReportOnce, { NewExportFormErrorState } from 'src/components/common/export-component/steps/two/once'
import ExportReportRecurring from 'src/components/common/export-component/steps/two/recurring'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { StyledFormControlLabel } from '../common'
import { useShareEmail } from 'src/lib/hooks/useShareEmail'
import { useUser } from 'src/lib/contexts/UserContext'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import { validateEmail } from 'src/lib/utils/helpers'

interface Step2ContentProps {
  config: ExportConfigStep2
  step1Config: ExportConfigStep1
  onChange: (config: ExportConfigStep2) => void
  onValidationChange: (isValid: boolean) => void
}

const initialFormErrorState: NewExportFormErrorState = {
  emails: [],
  email: {
    status: false,
    errorCode: ''
  },
  startDate: {
    status: false,
    errorCode: ''
  },
  endDate: {
    status: false,
    errorCode: ''
  },
  reportName: {
    status: false,
    errorCode: ''
  }
}

const Step2Content = ({ config, step1Config, onChange, onValidationChange }: Step2ContentProps) => {
  const { user } = useUser()

  const [formErrors, setFormErrors] = useState<NewExportFormErrorState>(initialFormErrorState)
  const [isReportNameTouched, setIsReportNameTouched] = useState(false)
  const [emailTouched, setEmailTouched] = useState(false)
  const [isStep1Valid, setIsStep1Valid] = useState(false)
  const [emailInputText, setEmailInputText] = useState('')
  const [emailInFocus, setEmailInFocus] = useState(false)

  const { emails, isValidating, emailError } = useShareEmail()

  const isExportingTodaysReport = step1Config.exportType === 'today'

  useEffect(() => {
    const hasEmailErrors = formErrors.emails.some(error => error.status)
    const hasReportNameErrors = !config.reportName || formErrors.reportName.status
    const isStep1Invalid = config.exportFrequency === 'once' && !isStep1Valid
    const hasEmailError = formErrors?.email?.status
    
    if (isExportingTodaysReport) {
      onValidationChange(!hasEmailError)
      return
    }
    
    onValidationChange(!hasEmailErrors && !hasReportNameErrors && !isStep1Invalid)
  }, [config.reportName, formErrors, onValidationChange, isStep1Valid, config.exportFrequency])

  const validateReportName = useCallback(
    (value: string) => {
      const isValid = value.trim().length > 0 && value.trim().length <= 60
      setFormErrors(prev => ({
        ...prev,
        reportName: {
          status: !isValid,
          errorCode: !isValid ? 'DC_FORM_001' : ''
        }
      }))
      onValidationChange(isValid)
      return isValid
    },
    [onValidationChange]
  )
  
  const validateEmailInput = useCallback((value: string) => {
    const isValid = validateEmail(value)
    setFormErrors(prev => ({
      ...prev,
      email: { status: !isValid, errorCode: !isValid ? 'DC_FORM_024' : '' }
    }))
  }, [])

  // Add this new function to check all validations
  const handleRecurringScheduleChange = (schedule: ExportConfigStep2['schedule']) => {
    onChange({
      ...config,
      schedule,
      // Clear dateRange when switching to recurring
      dateRange: {
        startDate: '',
        endDate: ''
      }
    })
  }

  useEffect(() => {
    if (!config.reportName) {
      validateReportName(config.reportName)
    }
    if (!emailInputText) {
      validateEmailInput(emailInputText)
    }
    if (config.exportFrequency === 'once' && isStep1Valid) {
      if (isExportingTodaysReport) {
        onValidationChange(!formErrors?.email?.status)
      } else {
        onValidationChange(true)
      }
    }
    onChange({
      ...config,
      email: user?.email,
      emails: user?.email ? [user.email] : []
    })
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {!isExportingTodaysReport && (
        <>
          <h2 className='text-base font-semibold'>Select type</h2>
          <FormControl component='fieldset'>
            <RadioGroup
              value={config.exportFrequency}
              onChange={e => {
                onChange({
                  ...config,
                  exportFrequency: e.target.value as 'once' | 'recurring'
                })
                validateReportName(config.reportName)
              }}
            >
              <StyledFormControlLabel value='once' control={<Radio />} label='Export only once' />
              <StyledFormControlLabel value='recurring' control={<Radio />} label='Recurring export' />
            </RadioGroup>
          </FormControl>

          <div className='flex flex-col mt-4'>
            {config.exportFrequency === 'once' && (
              <ExportReportOnce config={config} onChange={onChange} onValidationChange={setIsStep1Valid} />
            )}
            {config.exportFrequency === 'recurring' && (
              <ExportReportRecurring onChange={handleRecurringScheduleChange} schedule={config.schedule} />
            )}
          </div>
        </>
      )}

      {isExportingTodaysReport && (
        <>
          <h2 className='text-base font-semibold mt-4'>Send report to:</h2>
          <InputTextField
            textFieldId='export-email-input'
            inputPlaceholder={'Enter email address'}
            inputType='text'
            inputMargin={'none'}
            isFullWidth={true}
            errorStatus={!emailInFocus && emailTouched && formErrors?.email?.status}
            errorMessage={errorMappings?.[formErrors?.email?.errorCode as keyof typeof errorMappings]}
            inputValue={emailInputText}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onChange({
                ...config,
                email: event.target.value
              })
              setEmailInputText(event.target.value)
              validateEmailInput(event.target.value)
            }}
            handleBlur={() => {
              setEmailTouched(true)
              validateEmailInput(emailInputText)
              setEmailInFocus(false)
            }}
            handleFocus={() =>
              setEmailInFocus(true)
            }
          />

          <div className='flex items-center gap-2 bg-yellow-50 rounded-md p-2 border border-yellow-500'>
            <AlertTriangle size={20} className='text-yellow-600' />
            <p className='text-sm'>Today's report can only be sent to your email address</p>
          </div>
        </>
      )}

      {!isExportingTodaysReport && (
        <>
          {emailError ? (
            <>
              <h2 className='text-base font-semibold mt-4'>Send report to:</h2>
              <div className='flex items-center gap-2 bg-yellow-50 rounded-md p-2 border border-yellow-500'>
                <AlertTriangle size={20} className='text-yellow-600' />
                <p className='text-sm'>
                  You don't have access to download reports. Ask your store admin for permission
                </p>
              </div>
            </>
          ) : (
            <Box>
              <h2 className='text-base font-semibold mb-2 mt-4'>Send report to:</h2>
              {isValidating && !emailError ? (
                <Skeleton variant='text' width={200} height={70} sx={{ marginTop: '-10px' }} />
              ) : (
                <EmailMultiSelect
                  emails={emails}
                  initialSelectedEmails={user?.email ? [user.email] : []}
                  onSelectedEmailsChange={emails => {
                    onChange({
                      ...config,
                      emails
                    })
                  }}
                />
              )}
            </Box>
          )}
        </>
      )}
      {!isExportingTodaysReport && (
        <>
          <label className='text-coal-dark text-base font-semibold mt-4' htmlFor='report-name'>
            Name of report:
          </label>
          <InputTextField
            inputSize='small'
            textFieldId='report-name'
            isFullWidth={true}
            inputValue={config.reportName}
            inputProps={{ maxLength: 60 }}
            showCharacterCountConfig={{
              showOnlyOnFocus: false,
              maxLength: 60
            }}
            handleChange={(e: any) => {
              onChange({
                ...config,
                reportName: e.target.value
              })
              validateReportName(e.target.value)
            }}
            handleBlur={e => {
              validateReportName(e.target.value)
              setIsReportNameTouched(true)
            }}
            inputModeType='text'
            errorStatus={isReportNameTouched && formErrors.reportName.status}
            errorMessage={
              isReportNameTouched
                ? errorMappings?.[formErrors?.reportName?.errorCode as keyof typeof errorMappings]
                : undefined
            }
          />
        </>
      )}
    </Box>
  )
}

export default Step2Content
